import { useState } from 'react';
import { Loader2, Play, CheckCircle2, AlertTriangle, Zap } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

export function InvoicePdfCoveragePanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.invoicePdfCoverage();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const matchPct = result && result.total_bros_invoices > 0
    ? Math.round((result.matched_total / result.total_bros_invoices) * 100)
    : null;
  const hasMatches = result && result.matched_total > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Invoice PDF Coverage</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Reports how many BR OS invoices have a matched Xero twin with a fetchable PDF.
          Matching is tenant-scoped by invoice number with amount/date confidence checks.
        </p>
        <Button onClick={run} disabled={loading} size="sm">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
          Run Diagnostic
        </Button>

        {result && (
          <div className="space-y-4">
            {/* Verdict */}
            <div className={`flex items-start gap-2 p-3 rounded-lg border ${
              hasMatches
                ? 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-950'
                : 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950'
            }`}>
              {hasMatches
                ? <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
                : <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
              }
              <p className="text-sm" data-testid="pdf-coverage-verdict">
                {hasMatches
                  ? `${fmtInt(result.matched_total)} of ${fmtInt(result.total_bros_invoices)} BR OS invoices (${matchPct}%) matched a Xero twin with fetchable PDF.`
                  : `No BR OS invoices matched a Xero twin. PDF buttons will be disabled.`
                }
              </p>
            </div>

            {/* Counts grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="border rounded-lg p-3 space-y-1">
                <p className="text-sm font-medium">Coverage</p>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  <span className="text-muted-foreground">Total BR OS</span>
                  <span className="tabular-nums" data-testid="pdf-total-bros">{fmtInt(result.total_bros_invoices)}</span>
                  <span className="text-muted-foreground">Matched (exact)</span>
                  <span className="tabular-nums text-green-600">{fmtInt(result.matched_exact)}</span>
                  <span className="text-muted-foreground">Matched (number only)</span>
                  <span className="tabular-nums text-amber-600">{fmtInt(result.matched_number_only)}</span>
                  <span className="text-muted-foreground">Unmatched</span>
                  <span className="tabular-nums text-red-600">{fmtInt(result.unmatched)}</span>
                </div>
              </div>

              <div className="border rounded-lg p-3 space-y-1">
                <p className="text-sm font-medium">Xero Twins</p>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  <span className="text-muted-foreground">With external_id</span>
                  <span className="tabular-nums">{fmtInt(result.xero_twins_with_external_id)}</span>
                </div>
              </div>

              {result.unmatched_reasons && Object.keys(result.unmatched_reasons).length > 0 && (
                <div className="border rounded-lg p-3 space-y-1">
                  <p className="text-sm font-medium">Unmatched Reasons</p>
                  <div className="grid grid-cols-1 gap-0.5 text-xs">
                    {Object.entries(result.unmatched_reasons).map(([reason, count]) => (
                      <div key={reason} className="flex justify-between">
                        <span className="text-muted-foreground truncate mr-2">{reason}</span>
                        <span className="tabular-nums text-red-600">{fmtInt(count)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Matched samples */}
            {result.matched_samples && result.matched_samples.length > 0 && (
              <details>
                <summary className="text-sm font-medium cursor-pointer text-muted-foreground">
                  Matched samples ({result.matched_samples.length})
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1.5 font-medium">bros_id</th>
                        <th className="text-left px-2 py-1.5 font-medium">invoice_number</th>
                        <th className="text-center px-2 py-1.5 font-medium">confidence</th>
                        <th className="text-left px-2 py-1.5 font-medium">xero_external_id</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.matched_samples.map((s) => (
                        <tr key={s.bros_id} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono">{s.bros_id}</td>
                          <td className="px-2 py-1 font-mono">{s.invoice_number}</td>
                          <td className="px-2 py-1 text-center">
                            <Badge className={`text-[10px] ${s.confidence === 'exact' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'}`}>
                              {s.confidence}
                            </Badge>
                          </td>
                          <td className="px-2 py-1 font-mono text-[10px]">{s.xero_external_id}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            {/* Unmatched samples */}
            {result.unmatched_samples && result.unmatched_samples.length > 0 && (
              <details>
                <summary className="text-sm font-medium cursor-pointer text-muted-foreground">
                  Unmatched samples ({result.unmatched_samples.length})
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1.5 font-medium">bros_id</th>
                        <th className="text-left px-2 py-1.5 font-medium">invoice_number</th>
                        <th className="text-left px-2 py-1.5 font-medium">reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.unmatched_samples.map((s) => (
                        <tr key={s.bros_id} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono">{s.bros_id}</td>
                          <td className="px-2 py-1 font-mono">{s.invoice_number}</td>
                          <td className="px-2 py-1 text-muted-foreground">{s.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            <p className="text-[10px] text-muted-foreground">
              Generated: {result.generated_at}
            </p>
          </div>
        )}

        {/* Live Resolver Probe */}
        <LiveResolverProbe />
      </CardContent>
    </Card>
  );
}


function LiveResolverProbe() {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const preview = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.invoicePdfLiveProbe();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    if (!window.confirm(
      'Apply will re-run this probe against live Xero and persist every link it ' +
      'resolves. A bros_id number-fallback candidate that fails the amount/currency/' +
      'entity gate is never written, only reported as skipped_gated.'
    )) return;
    setApplying(true);
    try {
      const { data } = await diagnosticsAPI.invoicePdfLiveProbeApply();
      setResult(data);
      toast.success(
        `Applied: ${fmtInt(data.sample_resolved)} link(s) persisted.`
      );
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="space-y-3 border-t pt-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">Live Resolver Probe</p>
          <p className="text-xs text-muted-foreground">
            Queries Xero live for a sample of up to 10 unlinked BR OS invoices.
            Preview writes nothing. Apply persists resolved links (a bros_id
            number-fallback candidate is only ever linked if amount, currency,
            and entity all match; otherwise it is reported as skipped_gated).
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button onClick={preview} disabled={loading || applying} size="sm" variant="outline">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
            Preview
          </Button>
          <Button
            onClick={apply}
            disabled={!result || loading || applying}
            size="sm"
            data-testid="live-resolver-probe-apply"
          >
            {applying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Apply
          </Button>
        </div>
      </div>

      {result && (
        <div className="space-y-3">
          <Badge variant={result.mode === 'apply' ? 'default' : 'outline'} className="text-[10px]">
            {result.mode === 'apply' ? 'Apply run -- links persisted' : 'Preview -- nothing written'}
          </Badge>

          {/* Summary counts */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="border rounded-lg p-2 text-center">
              <p className="text-xs text-muted-foreground">Unlinked</p>
              <p className="text-lg font-semibold tabular-nums">{fmtInt(result.total_bros_unlinked)}</p>
            </div>
            <div className="border rounded-lg p-2 text-center">
              <p className="text-xs text-muted-foreground">Linked</p>
              <p className="text-lg font-semibold tabular-nums text-green-600">{fmtInt(result.total_bros_linked)}</p>
            </div>
            <div className="border rounded-lg p-2 text-center">
              <p className="text-xs text-muted-foreground">Resolved (number)</p>
              <p className="text-lg font-semibold tabular-nums text-green-600">{fmtInt(result.sample_resolved_via_invoice_number)}</p>
            </div>
            <div className="border rounded-lg p-2 text-center">
              <p className="text-xs text-muted-foreground">Resolved (bros_id)</p>
              <p className="text-lg font-semibold tabular-nums text-green-600">{fmtInt(result.sample_resolved_via_bros_id)}</p>
            </div>
            <div className="border rounded-lg p-2 text-center">
              <p className="text-xs text-muted-foreground">Already linked</p>
              <p className="text-lg font-semibold tabular-nums">{fmtInt(result.sample_already_linked)}</p>
            </div>
            <div className="border rounded-lg p-2 text-center">
              <p className="text-xs text-muted-foreground">Skipped (gated)</p>
              <p className="text-lg font-semibold tabular-nums text-amber-600">{fmtInt(result.sample_skipped_gated)}</p>
            </div>
            <div className="border rounded-lg p-2 text-center">
              <p className="text-xs text-muted-foreground">Missed</p>
              <p className="text-lg font-semibold tabular-nums text-red-600">{fmtInt(result.sample_missed)}</p>
            </div>
            <div className="border rounded-lg p-2 text-center">
              <p className="text-xs text-muted-foreground">Errors</p>
              <p className="text-lg font-semibold tabular-nums text-red-600">{fmtInt(result.sample_errors)}</p>
            </div>
          </div>

          {/* Per-sample results table */}
          {result.results && result.results.length > 0 && (
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-2 py-1.5 font-medium">invoice_number</th>
                    <th className="text-center px-2 py-1.5 font-medium">status</th>
                    <th className="text-left px-2 py-1.5 font-medium">via</th>
                    <th className="text-left px-2 py-1.5 font-medium">matched_field</th>
                    <th className="text-left px-2 py-1.5 font-medium">matched_tenant</th>
                    <th className="text-left px-2 py-1.5 font-medium">searched</th>
                    <th className="text-left px-2 py-1.5 font-medium">reason</th>
                  </tr>
                </thead>
                <tbody>
                  {result.results.map((r, i) => (
                    <tr key={r.bros_id || i} className="border-b last:border-0">
                      <td className="px-2 py-1 font-mono">{r.invoice_number}</td>
                      <td className="px-2 py-1 text-center">
                        <Badge className={`text-[10px] ${STATUS_BADGE_CLASS[r.status] || STATUS_BADGE_CLASS.default}`}>
                          {r.status}
                        </Badge>
                        {r.disambiguation_applied && (
                          <Badge className="text-[10px] ml-1 bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300">
                            disambig
                          </Badge>
                        )}
                      </td>
                      <td className="px-2 py-1 font-mono text-[10px]">{r.resolved_via || '-'}</td>
                      <td className="px-2 py-1 font-mono text-[10px]">{r.matched_tenant || '-'}</td>
                      <td className="px-2 py-1 text-[10px] text-muted-foreground">
                        {r.searched_tenants && r.searched_tenants.length > 0
                          ? `${r.searched_tenants.length} (${r.searched_tenants.map(t => t.name).join(', ')})`
                          : '-'
                        }
                      </td>
                      <td className="px-2 py-1 text-muted-foreground">
                        {r.gate_failure_reasons && r.gate_failure_reasons.length > 0
                          ? r.gate_failure_reasons.join(', ')
                          : (r.reason || '-')
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-[10px] text-muted-foreground">
            BUILD_COMMIT: {result.build_commit} | Generated: {result.generated_at}
          </p>
        </div>
      )}
    </div>
  );
}

const STATUS_BADGE_CLASS = {
  resolved: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  already_linked: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  skipped_gated: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
  missed: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  error: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  not_attempted: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  default: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
};
